import axios, {
  AxiosError,
  type AxiosInstance,
  type InternalAxiosRequestConfig,
  type AxiosResponse,
} from 'axios'
import {
  ApiException,
  ErrorCode,
  type ApiErrorEnvelope,
  type ApiSuccess,
  type FieldError,
} from './types'

/**
 * Streama CMS · HTTP client
 *
 * Single shared axios instance for all API calls. Handles four
 * cross-cutting concerns:
 *
 * 1. JWT injection on every request (from the auth Pinia store).
 * 2. Response envelope unwrap — successful responses return the typed
 *    payload directly via `response.data`. Failures throw ApiException
 *    with a normalized `code`, `status`, and `fieldErrors`.
 * 3. 401 / Spring-default-403 = session expiry → clear auth + redirect
 *    to /login with `?redirect=` preserving the current path.
 * 4. Application 403 (genuine privilege denial) → toast, stay put.
 *
 * Backend contract reference: this file matches the response shapes
 * captured in the v2 validation runbook (Part A). See types.ts for
 * the complete shape catalogue and the disambiguation logic that
 * separates "session expired" 403s (Spring Security default) from
 * "privilege denied" 403s (application-level @PreAuthorize).
 *
 * The auth and toast stores are imported lazily inside interceptors
 * to break the otherwise-circular dependency: stores import the
 * client to make calls, and the client wants to read/write stores.
 * Lazy import + Pinia post-init makes this safe.
 */

// -----------------------------------------------------------------
// Public surface
// -----------------------------------------------------------------

export const apiClient: AxiosInstance = axios.create({
  baseURL: '/api',
  timeout: 30_000,
  headers: {
    'Content-Type': 'application/json',
    Accept: 'application/json',
  },
})

// -----------------------------------------------------------------
// Request interceptor: attach JWT
// -----------------------------------------------------------------

apiClient.interceptors.request.use(
  async (config: InternalAxiosRequestConfig) => {
    const { useAuthStore } = await import('@/stores/auth')
    const auth = useAuthStore()
    if (auth.token) {
      config.headers.set('Authorization', `Bearer ${auth.token}`)
    }
    return config
  },
  (error) => Promise.reject(error),
)

// -----------------------------------------------------------------
// Response interceptor: unwrap + route errors
// -----------------------------------------------------------------

apiClient.interceptors.response.use(
  (response) => unwrapSuccess(response),
  async (error: AxiosError<ApiErrorEnvelope>) => {
    const status = error.response?.status ?? 0
    const envelope = error.response?.data
    const url = error.config?.url

    const exception = buildException(status, envelope, error.message)

    // 401, OR Spring-default 403 (= invalid token before
    // JwtAuthenticationEntryPoint backend patch lands).
    // Both mean "session is gone, send the user back to login."
    // Auth endpoints are exempted because failures there are normal
    // form errors (wrong password, etc.) and must surface to the
    // login form rather than redirect.
    const isSessionExpiry =
      exception.code === ErrorCode.UNAUTHORIZED ||
      exception.code === ErrorCode.SESSION_EXPIRED

    if (isSessionExpiry && !isAuthEndpoint(url)) {
      const { useAuthStore } = await import('@/stores/auth')
      const auth = useAuthStore()
      auth.clear()

      const { default: router } = await import('@/router')
      const currentPath = router.currentRoute.value.fullPath
      if (router.currentRoute.value.name !== 'login') {
        await router.push({
          name: 'login',
          query: { redirect: currentPath },
        })
      }
    }

    // Application 403 — genuine privilege denial. Distinct from
    // SESSION_EXPIRED above; here the user is authenticated but
    // lacks the privilege for this specific call.
    if (exception.code === ErrorCode.FORBIDDEN) {
      const { useToastStore } = await import('@/stores/toast')
      const { i18n } = await import('@/i18n')
      useToastStore().push({
        kind: 'error',
        message:
          i18n.global.t('error.forbidden') ?? 'Insufficient privileges.',
      })
    }

    throw exception
  },
)

// -----------------------------------------------------------------
// Helpers
// -----------------------------------------------------------------

/**
 * On HTTP 2xx, unwrap the `{data, message}` envelope so callers see
 * the typed payload directly via `response.data`.
 *
 * Some endpoints (downloads, blobs, debug routes) return non-JSON or
 * non-enveloped JSON. We pass those through unmodified — recognised
 * by `data` not being a plain object with a `data` field.
 */
function unwrapSuccess(response: AxiosResponse): AxiosResponse {
  const body = response.data
  if (
    typeof body === 'object' &&
    body !== null &&
    'data' in body &&
    !Array.isArray(body)
  ) {
    const envelope = body as ApiSuccess<unknown>
    response.data = envelope.data
  }
  return response
}

/**
 * Construct a normalised ApiException from whatever the backend sent
 * back. Three shapes to handle:
 *
 *   1. Standard error envelope: { status, error, message, fieldErrors? }
 *      → use `error` as code, map fieldErrors directly.
 *
 *   2. Spring Security default: { status, error: "Forbidden", path }
 *      → synthesise SESSION_EXPIRED so the SPA treats as auth failure.
 *      (Distinguished by error="Forbidden" + presence of `path` and
 *      absence of `fieldErrors`.)
 *
 *   3. No envelope (network error, plain text 500, etc.)
 *      → synthesise from HTTP status alone.
 */
function buildException(
  status: number,
  envelope: ApiErrorEnvelope | undefined,
  axiosMessage: string,
): ApiException {
  // Network / timeout / no response from server.
  if (status === 0 || !envelope) {
    return new ApiException(
      ErrorCode.NETWORK,
      axiosMessage || 'Network error',
      status,
    )
  }

  const fieldErrors: FieldError[] = envelope.fieldErrors ?? []
  const message = envelope.message ?? axiosMessage ?? 'Request failed'

  // Spring Security default envelope detection. The signature is
  // error="Forbidden" combined with the absence of fieldErrors and
  // presence of `path`. After the backend's JwtAuthenticationEntryPoint
  // lands, invalid-token responses come back as 401 with the
  // standard envelope and this branch becomes dead code.
  if (
    status === 403 &&
    envelope.error === 'Forbidden' &&
    !envelope.fieldErrors &&
    typeof envelope.path === 'string'
  ) {
    return new ApiException(
      ErrorCode.SESSION_EXPIRED,
      'Session expired. Please sign in again.',
      status,
    )
  }

  // Standard application envelope. Map the backend's `error` field to
  // a code; fall back to status-based synthesis if it's missing.
  const code = envelope.error ?? mapStatusToCode(status)

  return new ApiException(code, message, status, fieldErrors)
}

function mapStatusToCode(status: number): string {
  switch (status) {
    case 400:
      return ErrorCode.VALIDATION
    case 401:
      return ErrorCode.UNAUTHORIZED
    case 403:
      return ErrorCode.FORBIDDEN
    case 404:
      return ErrorCode.NOT_FOUND
    case 409:
      return ErrorCode.CONFLICT
    default:
      return status >= 500 ? ErrorCode.INTERNAL : ErrorCode.UNKNOWN
  }
}

/**
 * Auth endpoints get special treatment — a 401 from the login
 * endpoint means "wrong credentials," not "your session expired."
 * Those failures stay on the form rather than triggering a redirect.
 */
function isAuthEndpoint(url: string | undefined): boolean {
  if (!url) return false
  // The deployed backend mounts auth at /api/cms/users/login (etc.),
  // so we match both that path and the legacy /api/cms/auth/* prefix.
  return (
    url.startsWith('/cms/users/login') ||
    url.startsWith('/cms/users/refresh') ||
    url.startsWith('/cms/users/logout') ||
    url.startsWith('/cms/auth/')
  )
}
